import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import OnboardingWrapper from '@/components/ui/OnboardingWrapper';
import { formatPhoneNumber, validatePhoneNumber } from '@/utils/phoneUtils';
import { US_STATES_OPTIONS } from '@/utils/us-states';
import { EXTENDED_STEPS } from '@/utils/onboarding-steps';
import {
    useUpdateArchitectBusinessInfo,
    useUpdateSubcontractorBusinessInfo,
    useGetArchitect,
    useGetSubcontractor
} from '@/hooks/use-contractors';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';

// Extended onboarding steps for architect/subcontractor
// Using shared constant from utils

// Form validation schema
const formSchema = z.object({
    legalName: z.string().min(1, 'Legal company name is required'),
    dbaName: z.string().min(1, 'DBA company name is required'),
    ssn: z
        .string()
        .min(1, 'SSN or Federal EIN is required')
        .regex(/^\d{3}-\d{2}-\d{4}$/, 'SSN must be in format XXX-XX-XXXX'),
    website: z.string().optional(),
    phoneNumber: z
        .string()
        .min(1, 'Phone number is required')
        .refine(
            (phone) => validatePhoneNumber(phone),
            'Phone number must be in format (555) 123-4567'
        ),
    primaryEmail: z
        .string()
        .min(1, 'Email is required')
        .email('Please enter a valid email address'),
    streetAddress: z.string().min(1, 'Street address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zipCode: z
        .string()
        .min(5, 'ZIP code must be 5 digits')
        .max(5, 'ZIP code must be 5 digits')
        .regex(/^\d{5}$/, 'ZIP code must contain only numbers'),
});

type FormValues = z.infer<typeof formSchema>;

export default function BusinessInfoStep() {
    const [searchParams] = useSearchParams();
    const { handleError } = useErrorHandler();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const organizationId = searchParams.get('orgId');
    const accountType = searchParams.get('type') as 'architect' | 'subcontractor';

    // Fetch data based on account type
    const { data: architectData, isLoading: isLoadingArchitect } = useGetArchitect(
        accountType === 'architect' ? organizationId : null
    );
    const { data: subcontractorData, isLoading: isLoadingSubcontractor } = useGetSubcontractor(
        accountType === 'subcontractor' ? organizationId : null
    );

    const isLoadingData = isLoadingArchitect || isLoadingSubcontractor;
    const currentData = accountType === 'architect' ? architectData : subcontractorData;

    // API hooks for updates
    const updateArchitectBusinessInfo = useUpdateArchitectBusinessInfo();
    const updateSubcontractorBusinessInfo = useUpdateSubcontractorBusinessInfo();

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        mode: 'onChange', // This will clear errors as user types
        defaultValues: {
            legalName: '',
            dbaName: '',
            ssn: '',
            website: '',
            phoneNumber: '',
            primaryEmail: '',
            streetAddress: '',
            city: '',
            state: '',
            zipCode: '',
        },
    });

    // Handle missing orgId
    useEffect(() => {
        if (!organizationId) {
            toast({
                title: 'Missing Organization ID',
                description: 'Please complete the signup process first.',
                variant: 'warning',
            });
        }
    }, [organizationId]);

    // Prefill form with existing data
    useEffect(() => {
        if (!currentData) return;

        const specificProfile = accountType === 'architect'
            ? (currentData as any).architectSpecificProfile
            : (currentData as any).subcontractorSpecificProfile;
        const generalProfile = currentData.profile;

        if (specificProfile && (specificProfile.dbaName || specificProfile.ssn || specificProfile.primaryEmail)) {
            form.reset({
                legalName: currentData.name || '',
                dbaName: specificProfile.dbaName || '',
                ssn: specificProfile.ssn || '',
                website: specificProfile.website || '',
                phoneNumber: generalProfile?.phone || '',
                primaryEmail: specificProfile.primaryEmail || '',
                streetAddress: generalProfile?.address || '',
                city: generalProfile?.city || '',
                state: generalProfile?.state || '',
                zipCode: generalProfile?.zipCode || '',
            });
        }
    }, [currentData, form, accountType]);

    const handleNext = async (data: FormValues) => {
        console.log('BusinessInfoStep: handleNext called with data:', data);
        console.log('BusinessInfoStep: organizationId:', organizationId, 'accountType:', accountType);

        if (!organizationId) {
            console.log('BusinessInfoStep: No organizationId - allowing to proceed anyway');
            // Instead of returning false, let's proceed if validation passes
            // This might happen in development or testing scenarios
            toast({
                title: 'Warning',
                description: 'Organization ID is missing but proceeding...',
                variant: 'warning',
            });
            return true; // Allow navigation to continue
        }

        if (!accountType || (accountType !== 'architect' && accountType !== 'subcontractor')) {
            console.log('BusinessInfoStep: Invalid account type:', accountType);
            toast({
                title: 'Error',
                description: 'Invalid account type. Please try again.',
                variant: 'destructive',
            });
            return false;
        }

        setIsSubmitting(true);
        try {
            // Prepare the payload
            const payload = {
                legalName: data.legalName,
                dbaName: data.dbaName,
                ssn: data.ssn,
                phoneNumber: data.phoneNumber,
                website: data.website || '',
                primaryEmail: data.primaryEmail,
                streetAddress: data.streetAddress,
                city: data.city,
                state: data.state,
                zipCode: data.zipCode,
            };

            // Call the appropriate API based on account type
            if (accountType === 'architect') {
                await updateArchitectBusinessInfo.mutateAsync({
                    architectId: organizationId,
                    data: payload,
                });
            } else if (accountType === 'subcontractor') {
                await updateSubcontractorBusinessInfo.mutateAsync({
                    subcontractorId: organizationId,
                    data: payload,
                });
            }

            toast({
                title: 'Business Info Saved',
                description: 'Your business information has been saved successfully.',
                variant: 'success',
            });

            return true;
        } catch (error: any) {
            handleError(error, {
                customMessage: 'Failed to save business information. Please try again.',
            });
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <OnboardingWrapper
            currentStep={1}
            totalSteps={7}
            steps={EXTENDED_STEPS}
            accountType={accountType}
            onNext={async () => {
                console.log('BusinessInfoStep: onNext called');
                const isValid = await form.trigger();
                console.log('BusinessInfoStep: Form validation result:', isValid);

                if (!isValid) {
                    console.log('BusinessInfoStep: Form validation failed');
                    return false;
                }

                return new Promise((resolve) => {
                    console.log('BusinessInfoStep: Submitting form...');
                    form.handleSubmit(async (data) => {
                        console.log('BusinessInfoStep: Form submitted with data:', data);
                        const result = await handleNext(data);
                        console.log('BusinessInfoStep: handleNext result:', result);
                        resolve(result !== false);
                    })();
                });
            }}
            isLoading={isSubmitting}
            saveButtonText="Save & Continue"
        >
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
                        Business Information
                    </h2>
                    <p className="text-muted-foreground">
                        Please provide your company's basic information. This will be used for legal and billing purposes.
                    </p>
                </div>

                <Form {...form}>
                    <div className="space-y-6">
                        {/* Show loading state while fetching existing data */}
                        {isLoadingData && (
                            <div className="flex items-center justify-center py-4">
                                <div className="w-6 h-6 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
                                <span className="ml-2 text-muted-foreground">
                                    Loading existing data...
                                </span>
                            </div>
                        )}

                        {/* Legal Company Name and DBA */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="legalName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            Legal Name of Company<span className="text-red-600">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="e.g., ABC Architecture LLC"
                                                {...field}
                                                className="mt-1"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="dbaName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            DBA Company Name<span className="text-red-600">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Doing Business As"
                                                {...field}
                                                className="mt-1"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* SSN and Website */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="ssn"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            SSN or Federal EIN<span className="text-red-600">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="XXX-XX-XXXX"
                                                {...field}
                                                className="mt-1"
                                                onChange={(e) => {
                                                    let value = e.target.value.replace(/\D/g, '');
                                                    if (value.length >= 6) {
                                                        value = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5, 9);
                                                    } else if (value.length >= 4) {
                                                        value = value.slice(0, 3) + '-' + value.slice(3);
                                                    }
                                                    field.onChange(value);
                                                }}
                                                maxLength={11}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="website"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            Company Website
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="https://www.yourcompany.com"
                                                {...field}
                                                className="mt-1"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Phone and Email */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="phoneNumber"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            Company Phone Number<span className="text-red-600">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="(555) 123-4567"
                                                {...field}
                                                className="mt-1"
                                                onChange={(e) => {
                                                    const formatted = formatPhoneNumber(e.target.value);
                                                    field.onChange(formatted);
                                                }}
                                                maxLength={14}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="primaryEmail"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            Company Primary Email<span className="text-red-600">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="email"
                                                placeholder="contact@yourcompany.com"
                                                {...field}
                                                className="mt-1"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Address Section */}
                        <div className="space-y-4">
                            <FormField
                                control={form.control}
                                name="streetAddress"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            Street Address<span className="text-red-600">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="123 Main Street"
                                                {...field}
                                                className="mt-1"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FormField
                                    control={form.control}
                                    name="city"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-foreground font-medium">
                                                City<span className="text-red-600">*</span>
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="City"
                                                    {...field}
                                                    className="mt-1"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="state"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-foreground font-medium">
                                                State<span className="text-red-600">*</span>
                                            </FormLabel>
                                            <FormControl>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    value={field.value}
                                                    disabled={field.disabled}
                                                >
                                                    <SelectTrigger className="mt-1">
                                                        <SelectValue placeholder="Select state" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {US_STATES_OPTIONS.map((state) => (
                                                            <SelectItem key={state.value} value={state.value}>
                                                                {state.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="zipCode"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-foreground font-medium">
                                                ZIP Code<span className="text-red-600">*</span>
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="12345"
                                                    {...field}
                                                    className="mt-1"
                                                    maxLength={5}
                                                    pattern="[0-9]*"
                                                    inputMode="numeric"
                                                    onChange={(e) => {
                                                        const numericValue = e.target.value.replace(/\D/g, '');
                                                        field.onChange(numericValue);
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        </OnboardingWrapper>
    );
}
